/**
 * Surface des pistes : velours de damage, traces des skieurs, bosses, et
 * velours qui apparaît derrière les dameuses pendant la nuit.
 *
 * Deux textures lues par le shader du terrain (même grille que PisteRaster) :
 * - géométrie (une fois par tracé) : écart latéral au tracé (m, positif à
 *   droite en descendant), abscisse relative (0 en haut, 1 en bas), numéro de
 *   la piste et abscisse en mètres. Le numéro se lit au centre du texel (pas
 *   d'interpolation entre deux pistes), le reste est interpolé ;
 * - état (à chaque image, 256 pistes au plus) : moitié gauche et moitié
 *   droite damées cette nuit (fraction signée, voir groomProgress), fraîcheur
 *   du velours et bosses.
 *
 * Le velours est un côtelé fin parallèle au tracé, avec les raccords entre
 * passages de dameuse ; il s'efface quand le pixel devient plus large que les
 * côtes (pas de moirage au loin).
 */
import * as THREE from 'three/webgpu';
import { abs, float, floor, fract, fwidth, mix, positionWorld, select, sin, smoothstep, texture, vec2 } from 'three/tsl';
import { HF_CELL, HF_RES, MAP_HALF, groomProgress, moguls, type Groomer, type Heightfield, type Piste, type PisteTrack } from '@snowchain/shared';

const SLOTS = 256;
/** Période du côtelé (m) et largeur d'un passage de dameuse (m). */
const RIB = 0.9;
const LANE = 5.4;

class GroomSurface {
  /** Texture de géométrie, refaite par `configure` quand la carte change de taille. */
  geom: THREE.DataTexture;
  readonly state: THREE.DataTexture;
  private geomData: Uint16Array;
  private dist: Float32Array;
  private readonly stateData = new Uint16Array(SLOTS * 4);
  private readonly stateF = new Float32Array(SLOTS * 4);
  private slots = new Map<number, number>();
  private key = '';
  /** Grille du champ de hauteur de la carte jouée (Val Cendrée par défaut). */
  private res = HF_RES;
  private cell = HF_CELL;
  private half = MAP_HALF;

  constructor() {
    this.geomData = new Uint16Array(this.res * this.res * 4);
    this.dist = new Float32Array(this.res * this.res);
    this.geom = this.makeGeomTexture();
    this.state = new THREE.DataTexture(this.stateData, SLOTS, 1, THREE.RGBAFormat, THREE.HalfFloatType);
    this.state.magFilter = THREE.NearestFilter;
    this.state.minFilter = THREE.NearestFilter;
    this.state.generateMipmaps = false;
    this.state.needsUpdate = true;
  }

  private makeGeomTexture(): THREE.DataTexture {
    const tex = new THREE.DataTexture(this.geomData, this.res, this.res, THREE.RGBAFormat, THREE.HalfFloatType);
    tex.magFilter = THREE.LinearFilter;
    tex.minFilter = THREE.LinearFilter;
    tex.generateMipmaps = false;
    tex.wrapS = THREE.ClampToEdgeWrapping;
    tex.wrapT = THREE.ClampToEdgeWrapping;
    tex.needsUpdate = true;
    return tex;
  }

  /**
   * Aligne la grille sur le champ de hauteur de la carte. À appeler avant de
   * créer le matériau du terrain : `shade` y fige la taille de la grille.
   */
  configure(hf: Heightfield): void {
    if (hf.res === this.res && hf.cell === this.cell && hf.half === this.half) return;
    this.res = hf.res;
    this.cell = hf.cell;
    this.half = hf.half;
    this.geomData = new Uint16Array(this.res * this.res * 4);
    this.dist = new Float32Array(this.res * this.res);
    this.geom.dispose();
    this.geom = this.makeGeomTexture();
    this.key = '';
    this.slots = new Map();
  }

  /** Recalcule la texture de géométrie si les tracés ont changé. */
  updateGeometry(pistes: Iterable<Piste>, trackOf: (id: number) => PisteTrack | null): void {
    const list = [...pistes].sort((a, b) => a.id - b.id).slice(0, SLOTS - 1);
    const key = list.map((p) => `${p.id}:${p.path.length}:${p.width}:${p.path[0]?.join(',')}:${p.path[p.path.length - 1]?.join(',')}`).join('|');
    if (key === this.key) return;
    this.key = key;
    this.slots = new Map(list.map((p, i) => [p.id, i + 1]));
    this.geomData.fill(0);
    this.dist.fill(Infinity);
    for (const p of list) {
      const track = trackOf(p.id);
      if (track) this.rasterize(track, this.slots.get(p.id) as number);
    }
    this.geom.needsUpdate = true;
  }

  private rasterize(track: PisteTrack, slot: number): void {
    const { xs, zs, cum } = track.path;
    const reach = track.width / 2 + 5;
    const length = track.length || 1;
    const toHalf = THREE.DataUtils.toHalfFloat;
    for (let s = 0; s < xs.length - 1; s++) {
      const ax = xs[s] as number;
      const az = zs[s] as number;
      const bx = xs[s + 1] as number;
      const bz = zs[s + 1] as number;
      const dx = bx - ax;
      const dz = bz - az;
      const l2 = dx * dx + dz * dz;
      if (l2 < 1e-6) continue;
      const len = Math.sqrt(l2);
      const s0 = cum[s] as number;
      const s1 = cum[s + 1] as number;
      const { res, cell, half } = this;
      const i0 = Math.max(0, Math.floor((Math.min(ax, bx) - reach + half) / cell));
      const i1 = Math.min(res - 1, Math.ceil((Math.max(ax, bx) + reach + half) / cell));
      const j0 = Math.max(0, Math.floor((Math.min(az, bz) - reach + half) / cell));
      const j1 = Math.min(res - 1, Math.ceil((Math.max(az, bz) + reach + half) / cell));
      for (let j = j0; j <= j1; j++) {
        const z = -half + j * cell;
        for (let i = i0; i <= i1; i++) {
          const x = -half + i * cell;
          let t = ((x - ax) * dx + (z - az) * dz) / l2;
          t = t < 0 ? 0 : t > 1 ? 1 : t;
          const cx = ax + dx * t;
          const cz = az + dz * t;
          const d = Math.hypot(x - cx, z - cz);
          const k = j * res + i;
          if (d > reach || d >= (this.dist[k] as number)) continue;
          this.dist[k] = d;
          // Écart latéral signé : normale droite de la direction de descente (dz, -dx).
          const lateral = ((x - cx) * dz - (z - cz) * dx) / len;
          const along = s0 + (s1 - s0) * t;
          const o = k * 4;
          this.geomData[o] = toHalf(lateral);
          this.geomData[o + 1] = toHalf(along / length);
          this.geomData[o + 2] = toHalf(slot);
          this.geomData[o + 3] = toHalf(along);
        }
      }
    }
  }

  /** État de chaque piste à l'instant de jeu t (velours, bosses, damage en cours). */
  updateState(pistes: Map<number, Piste>, groomers: Iterable<Groomer>, t: number): void {
    const f = this.stateF;
    const next = new Float32Array(f.length);
    for (const [id, slot] of this.slots) {
      const p = pistes.get(id);
      if (!p) continue;
      const o = slot * 4;
      next[o + 2] = p.groomed ?? 0;
      next[o + 3] = moguls({ quality: p.quality, groomed: p.groomed ?? 0 });
    }
    for (const g of groomers) {
      const pr = groomProgress(g, t);
      if (!pr) continue;
      const slot = this.slots.get(pr.pisteId);
      if (!slot) continue;
      next[slot * 4] = pr.left;
      next[slot * 4 + 1] = pr.right;
    }
    let changed = false;
    for (let k = 0; k < next.length; k++) {
      if (Math.abs((next[k] as number) - (f[k] as number)) > 1e-4) {
        changed = true;
        break;
      }
    }
    if (!changed) return;
    f.set(next);
    const toHalf = THREE.DataUtils.toHalfFloat;
    for (let k = 0; k < f.length; k++) this.stateData[k] = toHalf(f[k] as number);
    this.state.needsUpdate = true;
  }

  /**
   * Teinte de la neige damée d'une piste, modulée par le velours, les traces,
   * les bosses et le damage en cours. `noise` : bruit de détail du terrain (0..1).
   */
  shade(color: THREE.Node<'vec3'>, noise: THREE.Node<'float'>): THREE.Node<'vec3'> {
    const { res, cell, half } = this;
    const raw = vec2(
      positionWorld.x.add(half).div(cell).add(0.5).div(res),
      positionWorld.z.add(half).div(cell).add(0.5).div(res),
    );
    const g = texture(this.geom, raw);
    const snapped = floor(raw.mul(res)).add(0.5).div(res);
    const slot = floor(texture(this.geom, snapped).b.add(0.5));
    const st = texture(this.state, vec2(slot.add(0.5).div(SLOTS), 0.5));
    const lateral = g.r;
    const v = g.g;
    const along = g.a;
    // Damage de cette nuit : moitié gauche ou droite, depuis le bas (f > 0) ou depuis le haut (f < 0).
    const f = select(lateral.lessThan(0), st.r, st.g);
    const e = float(0.004);
    const fromBottom = smoothstep(float(1).sub(f).sub(e), float(1).sub(f).add(e), v);
    const fromTop = float(1).sub(smoothstep(f.negate().sub(e), f.negate().add(e), v));
    const partial = select(f.greaterThan(0), fromBottom, select(f.lessThan(0), fromTop, float(0)));
    const fresh = select(abs(f).greaterThan(0.999), float(1), partial);
    const velours = mix(st.b, float(1), fresh);
    const bumps = mix(st.a, float(0), fresh);
    // Côtelé fin et raccords entre passages, effacés quand ils deviennent plus fins qu'un pixel.
    const w = fwidth(lateral);
    const ribs = sin(lateral.mul((2 * Math.PI) / RIB)).mul(float(1).sub(smoothstep(RIB * 0.22, RIB * 0.55, w)));
    const seamDist = abs(fract(lateral.div(LANE).add(0.5)).sub(0.5)).mul(LANE);
    const seams = float(1).sub(smoothstep(0.06, 0.28, seamDist)).mul(float(1).sub(smoothstep(0.08, 0.25, w)));
    const corduroy = ribs.mul(0.035).sub(seams.mul(0.06)).mul(velours);
    // Traces de skieurs sur une neige usée.
    const traces = noise.sub(0.5).mul(0.09).mul(float(1).sub(velours));
    // Bosses : relief en quinconce déformé par le bruit (pas de quadrillage régulier), ombré d'un
    // côté, dans le repère de la piste ; effacé avant que le motif ne moire au loin.
    const wa = fwidth(along);
    const warp = noise.sub(0.5).mul(2.4);
    const mog = sin(along.mul((2 * Math.PI) / 5.5).add(warp).add(sin(lateral.mul(0.7)).mul(1.3))).mul(sin(lateral.mul((2 * Math.PI) / 4.6).add(along.mul(0.31)).sub(warp.mul(0.8))));
    const bumpShade = mog.mul(0.05).sub(0.03).mul(bumps).mul(float(1).sub(smoothstep(0.45, 1.3, wa)));
    const onPiste = slot.greaterThan(0.5);
    const factor = float(1).add(corduroy).add(traces).add(bumpShade).add(velours.mul(0.025));
    return mix(color, color.mul(factor), select(onPiste, float(1), float(0)));
  }
}

export const groomSurface = new GroomSurface();